import {
  FirestoreDataConverter,
  QueryDocumentSnapshot,
  SnapshotOptions,
  Timestamp,
} from "firebase/firestore";
import {
  CampaignStatus,
  CampaignType,
  MarketingCampaign,
  emptyAudienceFilter,
  MARKETING_CAMPAIGN_FROM_EMAIL,
  MARKETING_CAMPAIGN_FROM_NAME,
  parseAudienceFilterGroup,
  parseCampaignRepeat,
  parseReplyToUserIds,
  parseSendOccurrence,
} from "../leads/marketingCampaign";
import { parseCampaignFeaturedVehicleLayout } from "../leads/campaignFeaturedVehicles";

const VALID_TYPES: CampaignType[] = ["email", "sms"];
const VALID_STATUSES: CampaignStatus[] = [
  "draft",
  "scheduled",
  "sending",
  "sent",
  "paused",
  "cancelled",
];

const parseType = (value: unknown): CampaignType =>
  typeof value === "string" && (VALID_TYPES as string[]).includes(value)
    ? (value as CampaignType)
    : "email";

const parseStatus = (value: unknown): CampaignStatus =>
  typeof value === "string" && (VALID_STATUSES as string[]).includes(value)
    ? (value as CampaignStatus)
    : "draft";

const convertTimestamp = (timestamp: unknown): Date | null => {
  if (!timestamp) return null;
  if (timestamp instanceof Timestamp) return timestamp.toDate();
  if (timestamp instanceof Date) return Number.isNaN(timestamp.getTime()) ? null : timestamp;
  if (typeof timestamp === "string") {
    const parsed = new Date(timestamp);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
};

const serializeDate = (d: Date): string =>
  d instanceof Date && !Number.isNaN(d.getTime()) ? d.toISOString() : new Date().toISOString();

const serializeDateNullable = (d: Date | null | undefined): string | null => {
  if (!d) return null;
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return null;
  return d.toISOString();
};

export const marketingCampaignConverter: FirestoreDataConverter<MarketingCampaign> = {
  toFirestore(campaign: MarketingCampaign) {
    const { id: _id, ...rest } = campaign;
    const createdAt =
      rest.createdAt instanceof Date && !Number.isNaN(rest.createdAt.getTime())
        ? rest.createdAt
        : new Date();
    const updatedAt =
      rest.updatedAt instanceof Date && !Number.isNaN(rest.updatedAt.getTime())
        ? rest.updatedAt
        : createdAt;
    const audienceFilter = parseAudienceFilterGroup(rest.audienceFilter);
    const savedAudienceId =
      typeof rest.savedAudienceId === "string" ? rest.savedAudienceId.trim() : "";
    const replyToUserIds = parseReplyToUserIds(rest.replyToUserIds);
    const includedContactIds = parseReplyToUserIds(rest.includedContactIds);
    const excludedContactIds = parseReplyToUserIds(rest.excludedContactIds);
    const featuredVehicleIds = parseReplyToUserIds(rest.featuredVehicleIds);
    const featuredVehicleLayout = parseCampaignFeaturedVehicleLayout(rest.featuredVehicleLayout);
    return {
      name: rest.name?.trim() ?? "",
      type: parseType(rest.type),
      fromName: MARKETING_CAMPAIGN_FROM_NAME,
      fromEmail: MARKETING_CAMPAIGN_FROM_EMAIL,
      ...(replyToUserIds.length > 0 ? { replyToUserIds } : {}),
      subject: rest.subject?.trim() ?? "",
      htmlBody: typeof rest.htmlBody === "string" ? rest.htmlBody : "",
      audienceFilter,
      ...(savedAudienceId ? { savedAudienceId } : {}),
      includedContactIds,
      excludedContactIds,
      featuredVehicleIds,
      ...(featuredVehicleLayout ? { featuredVehicleLayout } : {}),
      archived: rest.archived === true,
      status: parseStatus(rest.status),
      scheduledAt: serializeDateNullable(rest.scheduledAt ?? null),
      repeat: parseCampaignRepeat(rest.repeat),
      sendOccurrence: parseSendOccurrence(rest.sendOccurrence),
      sentCount: Number.isFinite(rest.sentCount) ? Number(rest.sentCount) : 0,
      failedCount: Number.isFinite(rest.failedCount) ? Number(rest.failedCount) : 0,
      unsubscribedCount: Number.isFinite(rest.unsubscribedCount)
        ? Number(rest.unsubscribedCount)
        : 0,
      sentAt: serializeDateNullable(rest.sentAt ?? null),
      createdByUserId: rest.createdByUserId ?? "",
      createdAt: serializeDate(createdAt),
      updatedAt: serializeDate(updatedAt),
    };
  },

  fromFirestore(
    snapshot: QueryDocumentSnapshot,
    options?: SnapshotOptions,
  ): MarketingCampaign {
    const data = snapshot.data(options) ?? {};
    const createdAt = convertTimestamp(data.createdAt) || new Date();
    const savedAudienceId =
      typeof data.savedAudienceId === "string" ? data.savedAudienceId.trim() : "";
    const replyToUserIds = parseReplyToUserIds(data.replyToUserIds);
    const includedContactIds = parseReplyToUserIds(data.includedContactIds);
    const excludedContactIds = parseReplyToUserIds(data.excludedContactIds);
    const featuredVehicleIds = parseReplyToUserIds(data.featuredVehicleIds);
    const featuredVehicleLayout = parseCampaignFeaturedVehicleLayout(data.featuredVehicleLayout);
    return {
      id: snapshot.id,
      name: typeof data.name === "string" ? data.name : "",
      type: parseType(data.type),
      fromName: typeof data.fromName === "string" ? data.fromName : "",
      fromEmail: typeof data.fromEmail === "string" ? data.fromEmail : "",
      ...(replyToUserIds.length > 0 ? { replyToUserIds } : {}),
      subject: typeof data.subject === "string" ? data.subject : "",
      htmlBody: typeof data.htmlBody === "string" ? data.htmlBody : "",
      audienceFilter: data.audienceFilter
        ? parseAudienceFilterGroup(data.audienceFilter)
        : emptyAudienceFilter(),
      ...(savedAudienceId ? { savedAudienceId } : {}),
      ...(includedContactIds.length > 0 ? { includedContactIds } : {}),
      ...(excludedContactIds.length > 0 ? { excludedContactIds } : {}),
      ...(featuredVehicleIds.length > 0 ? { featuredVehicleIds } : {}),
      ...(featuredVehicleLayout ? { featuredVehicleLayout } : {}),
      ...(data.archived === true ? { archived: true } : {}),
      status: parseStatus(data.status),
      scheduledAt: convertTimestamp(data.scheduledAt),
      repeat: parseCampaignRepeat(data.repeat),
      sendOccurrence: parseSendOccurrence(data.sendOccurrence),
      sentCount: typeof data.sentCount === "number" && Number.isFinite(data.sentCount) ? data.sentCount : 0,
      failedCount:
        typeof data.failedCount === "number" && Number.isFinite(data.failedCount) ? data.failedCount : 0,
      unsubscribedCount:
        typeof data.unsubscribedCount === "number" && Number.isFinite(data.unsubscribedCount)
          ? data.unsubscribedCount
          : 0,
      sentAt: convertTimestamp(data.sentAt),
      createdByUserId:
        typeof data.createdByUserId === "string" ? data.createdByUserId : "",
      createdAt,
      updatedAt: convertTimestamp(data.updatedAt) || createdAt,
    };
  },
};
